/**
 * La découpe à l'export : quels calques sont des tracés de découpe, et ce que
 * le backend reçoit pour les poser en ton direct vectoriel /CutContour
 * (`backend/app/print_pro.py`).
 *
 * Deux règles :
 *   1. dans un PDF, un calque de découpe n'est JAMAIS fondu dans l'image : il y
 *      revient en ton direct vectoriel, à 100 % de l'encre choisie (C, M, J ou
 *      N), quel que soit le préréglage. Il reste visible à l'écran (sa couleur de
 *      remplacement), et l'Epson, qui reconnaît le nom CutContour, ne l'imprime
 *      pas. Fondu, ce n'était plus que du rose parmi d'autres pixels : sur le
 *      préréglage RVB par défaut, il partait à l'impression ;
 *   2. le PNG, lui, le garde peint, comme à l'écran : il sert de repère (BAT) et
 *      ne part jamais à l'Epson (Redouane, 13/10). Un PNG n'a pas de plaques,
 *      il ne pourrait pas le porter autrement.
 */
import { flattenForRender } from './layerTree';

/** Les encres du ton direct, à 100 % ; le magenta est celui des gabarits. */
export const ENCRES_TON_DIRECT = ['C', 'M', 'J', 'N'];
export const ENCRE_PAR_DEFAUT = 'M';

/**
 * Un calque est-il un tracé de découpe ? Le drapeau `isCutContour` (gabarits,
 * import PDF, générateur), avec l'heuristique de secours des projets anciens :
 * un trait non rempli nommé « Découpe… » ou rangé dans le groupe « Découpe ».
 */
export function estCalqueDecoupe(el, parId) {
    if (el.type !== 'shape' || el.svgFill !== false || !el.svgPath) return false;
    if (el.isCutContour) return true;
    if (/^découpe/i.test(el.name || '')) return true;
    const parent = el.parentId ? parId.get(el.parentId) : null;
    return !!(parent && parent.type === 'group' && /^découpe/i.test(parent.name || ''));
}

/**
 * Les calques de découpe d'un document.
 * `tous` : à retirer de toute image. `imprimes` : ceux qui partent en ton direct
 * — un calque masqué (lui ou son groupe) n'est pas imprimé, il n'est pas découpé.
 */
export function calquesDecoupe(elements) {
    const els = elements || [];
    const parId = new Map(els.map((e) => [e.id, e]));
    const tous = els.filter((e) => estCalqueDecoupe(e, parId));
    const ids = new Set(tous.map((e) => e.id));
    const imprimes = flattenForRender(els).filter((x) => ids.has(x.el.id) && !x.effHidden).map((x) => x.el);
    return { tous, imprimes };
}

/** Le document sans ses calques de découpe : ce que le moteur de rendu peint. */
export function sansDecoupe(elements) {
    const { tous } = calquesDecoupe(elements);
    const ids = new Set(tous.map((e) => e.id));
    return (elements || []).filter((e) => !ids.has(e.id));
}

/**
 * Ce que le moteur de rendu peint pour un format : le PDF sans ses tracés de
 * découpe (ils reviennent sur leur plaque), le PNG tel qu'à l'écran.
 */
export function elementsAPeindre(elements, format) {
    return format === 'pdf' ? sansDecoupe(elements) : (elements || []);
}

/** L'encre d'une découpe : celle choisie dans l'inspecteur, sinon le magenta des gabarits. */
export function encreDe(el) {
    const e = el?.decoupe?.encre;
    return ENCRES_TON_DIRECT.includes(e) ? e : ENCRE_PAR_DEFAUT;
}

/**
 * Ce qui empêche de poser la découpe telle qu'on la voit, en une phrase ; `null`
 * si rien. Le ton direct est UN nom (CutContour) donc UNE encre ; et le backend
 * ne sait pas encore tourner un tracé (il le poserait droit, à côté de l'image).
 */
export function refusDecoupe(calques) {
    const tourne = calques.find((el) => ((Number(el.rotation) || 0) % 360 + 360) % 360 !== 0);
    if (tourne) {
        return `Le tracé de découpe « ${tourne.name || tourne.id} » est tourné de ${tourne.rotation}° : l'export ne sait pas encore poser une découpe tournée. Remettre sa rotation à 0 avant d'exporter.`;
    }
    const encres = [...new Set(calques.map(encreDe))];
    if (encres.length > 1) {
        return `Les tracés de découpe demandent ${encres.length} encres différentes (${encres.join(', ')}) : le ton direct CutContour n'en a qu'une. Choisir la même encre pour tous.`;
    }
    return null;
}

/** Les tracés tels que `print_pro._draw_cutcontour` les attend, encre comprise. */
export function cheminsDecoupe(calques) {
    return calques.map((el) => ({
        d: el.svgPath,
        view_box: el.svgViewBox,
        x_mm: el.x, y_mm: el.y,
        w_mm: el.width, h_mm: el.height,
        stroke_width: el.strokeWidth,
        dasharray: el.strokeDasharray || null,
        encre: encreDe(el),
    }));
}
